import { activityLogger } from '@databases/logs/activity-logger';
import { container } from '@databases/service-container';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { type DatabaseBridgeAPI, getBridgeAPI } from '@databases/utils/database/bridge';
import React, {
  createContext,
  useState,
  type PropsWithChildren,
  type FC,
  useCallback,
  useEffect,
} from 'react';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { currentDrawerPanel } from '@canvas/sidebar-panel-state';

type AllowedPanelTypes = {
  ViewSettingsPanel: {
    canEditViews: boolean;
    fieldIdForFilterToCreate?: string;
  };
  FieldCreatePanel: {
    type: DatabaseFieldType;
    field: DatabaseField<DatabaseFieldType>;
    disableMulti?: boolean;
    enforceSelectOption?: boolean;
  };
  FieldSettingsPanel: {
    fieldId: string;
  };
  EntryViewPanel: {
    entryId: string;
    contentId?: string;
    noDelete?: boolean;
    isReadonly?: boolean;
  };
  DatabaseSetupPanel: undefined;
  CopyDatabasePanel: undefined;
  DevtoolsPanel: undefined;
};

type AllowedPanelKeys = keyof AllowedPanelTypes;

type PanelDataType = AllowedPanelTypes[AllowedPanelKeys];

export type PanelsType = {
  [key in AllowedPanelKeys]?: React.ReactNode;
};

export type PanelUnsavedChangesStateType = {
  [key in AllowedPanelKeys]: boolean;
};

export type ShowPanelType = <key extends AllowedPanelKeys>(
  panelKey: key,
  data?: AllowedPanelTypes[key]
) => void;

export interface PanelContextInterface {
  showPanel: ShowPanelType;
  hidePanel: () => void;
  getData: <T extends AllowedPanelKeys>() => AllowedPanelTypes[T];
  getPanelComponent: () => React.ReactNode;
  getPanelKey: () => AllowedPanelKeys | undefined;
  isPanelVisible: (panelKey?: AllowedPanelKeys) => boolean;
}

type PanelType = {
  panelKey: AllowedPanelKeys | undefined;
  component: React.ReactNode;
  data: PanelDataType;
};

export const PanelContext = createContext<PanelContextInterface>({
  showPanel: () => {},
  hidePanel: () => {},
  getData: <T,>() => null as T,
  getPanelComponent: () => null,
  getPanelKey: () => undefined,
  isPanelVisible: () => false,
});

export const PanelProvider: FC<
  PropsWithChildren<{
    defaultPanels: PanelsType;
  }>
> = ({ children, defaultPanels }) => {
  const isContentWrapperEnabled = isFeatureFlagEnabled(
    'databases-frontend-content-wrapper'
  );
  const [panels] = useState<PanelsType>(defaultPanels);
  const [bridgeApi, setBridgeApi] = useState<DatabaseBridgeAPI | null>(null);
  const [panel, setPanel] = useState<PanelType>({
    component: null,
    data: undefined,
    panelKey: undefined,
  });

  const showPanel: ShowPanelType = useCallback(
    (panelKey, data) => {
      setPanel({ component: panels[panelKey], data, panelKey });
      activityLogger.log('showPanel', { panelKey: panel.panelKey, ...data });
    },
    [panel.panelKey, panels]
  );

  const hidePanel = useCallback(() => {
    setPanel({ component: null, data: undefined, panelKey: undefined });
    activityLogger.log('hidePanel', { panelKey: panel.panelKey });
  }, [panel.panelKey]);

  /**
   * While we transition to an isolated Panels Manager and away from the PanelProvider, this is a temporary
   * solution to register the CloseSidePanels listener.
   * This should only be used in the current PanelProvider.
   */
  useEffect(() => {
    const eventBus = container.resolve('EventBus');

    eventBus.on('databaseContentIdChanged', hidePanel);

    return () => eventBus.remove('databaseContentIdChanged', hidePanel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isContentWrapperEnabled) {
      const loadBridgeApi = async () => {
        await getBridgeAPI().then(setBridgeApi);
      };
      loadBridgeApi();
    }
  }, [panel.component, isContentWrapperEnabled]);

  useEffect(() => {
    if (isContentWrapperEnabled && bridgeApi?.objectSidebarMethods) {
      // Navigation between comments in Confluence panel may open entry view panel
      if (
        panel.panelKey === 'EntryViewPanel' &&
        currentDrawerPanel.panel === 'commentsPanel'
      ) {
        return;
      }

      if (panel.component !== null) {
        bridgeApi.objectSidebarMethods.hideObjectSidebarPanel?.();
        bridgeApi.objectSidebarMethods.updateObjectSidebarControlState?.(false);
      } else {
        bridgeApi.objectSidebarMethods.updateObjectSidebarControlState?.(true);
      }
    }
  }, [panel.component, bridgeApi, isContentWrapperEnabled, panel.panelKey]);

  const isPanelVisible = useCallback(
    (panelKey?: AllowedPanelKeys) => {
      if (!panelKey) {
        return panel.component !== null;
      }

      if (panel.component === null) {
        return false;
      }

      return panel.panelKey === panelKey;
    },
    [panel.component, panel.panelKey]
  );

  const getData = useCallback(<T,>(): T => panel.data as T, [panel.data]);
  const getPanelComponent = useCallback(() => panel.component, [panel.component]);
  const getPanelKey = useCallback(() => panel.panelKey, [panel.panelKey]);

  return (
    <PanelContext.Provider
      value={{
        showPanel,
        hidePanel,
        getData,
        getPanelComponent,
        isPanelVisible,
        getPanelKey,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};
